import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, Calendar, CreditCard, Download } from 'lucide-react';
import { wholesaleService } from '../../services/wholesaleService';
import { useAuth } from '../../contexts/AuthContext';

const OrderConfirmation = () => {
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    // Ensure user is authenticated and orderId exists
    if (!user) {
      navigate('/login');
      return;
    }

    if (!location.state?.orderId) {
      console.log('No order ID found in location state:', location.state);
      navigate('/wholesale/catalog');
      return;
    }

    fetchOrderDetails();
  }, [user, location.state, navigate]);

  const fetchOrderDetails = async () => {
    try {
      console.log('Fetching order details for ID:', location.state.orderId);
      const response = await wholesaleService.getOrder(location.state.orderId);
      console.log('Order details response:', response);
      
      if (!response.order) {
        throw new Error('No order data received');
      }
      setOrderDetails(response.order);
    } catch (error) {
      console.error('Error fetching order details:', error);
      setError('Unable to load order details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger" role="alert">
          {error}
          <button 
            className="btn btn-outline-primary mt-3 d-block"
            onClick={() => navigate('/wholesale/orders')}
          >
            View All Orders
          </button>
        </div>
      </div>
    );
  }

  // Make sure we have order details before rendering
  if (!orderDetails) {
    return (
      <div className="container mt-5">
        <div className="alert alert-warning" role="alert">
          Order details not found.
          <button 
            className="btn btn-outline-primary mt-3 d-block"
            onClick={() => navigate('/wholesale/orders')}
          >
            View All Orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container my-5">
      <div className="text-center mb-4">
        <CheckCircle className="text-success mb-3" size={64} />
        <h2>Order Confirmed!</h2>
        <p className="lead">Thank you for your order. Here's your confirmation.</p>
      </div>

      <div className="row justify-content-center">
        <div className="col-md-8">
          {/* Order Summary Card */}
          <div className="card mb-4">
            <div className="card-header bg-light">
              <h5 className="mb-0">Order Summary #{orderDetails.orderId || orderDetails.orderID}</h5>
            </div>
            <div className="card-body">
              <div className="row mb-4">
                <div className="col-md-6">
                  <h6 className="text-muted mb-3">
                    <Calendar size={18} className="me-2" />
                    Payment Schedule
                  </h6>
                  <p className="mb-1">Order Date: {new Date(orderDetails.orderDate).toLocaleDateString()}</p>
                  <p className="mb-0">Payment Due: {new Date(orderDetails.paymentDueDate).toLocaleDateString()}</p>
                </div>
                {orderDetails.paymentMethod && (
                  <div className="col-md-6">
                    <h6 className="text-muted mb-3">
                      <CreditCard size={18} className="me-2" />
                      Payment Method
                    </h6>
                    <p className="mb-1">
                      {orderDetails.paymentMethod.brand} ending in {orderDetails.paymentMethod.last4}
                    </p>
                    <p className="mb-0">Pre-authorization complete</p>
                  </div>
                )}
              </div>

              <table className="table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th className="text-center">Quantity</th>
                    <th className="text-end">Price</th>
                    <th className="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {orderDetails.items?.map(item => (
                    <tr key={item.orderItemID || item.productId}>
                      <td>
                        {item.name}
                        <br />
                        <small className="text-muted">
                          {item.vintage} {item.varietal}
                        </small>
                      </td>
                      <td className="text-center">{item.quantity} cases</td>
                      <td className="text-end">${Number(item.pricePerUnit || item.price).toFixed(2)}</td>
                      <td className="text-end">${Number(item.totalPrice || (item.quantity * item.price)).toFixed(2)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan="3" className="text-end">
                      <strong>Total Amount</strong>
                    </td>
                    <td className="text-end">
                      <strong>${Number(orderDetails.totalAmount).toFixed(2)}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Shipping Information */}
          {orderDetails.shippingAddress && (
            <div className="card mb-4">
              <div className="card-header bg-light">
                <h5 className="mb-0">Shipping Information</h5>
              </div>
              <div className="card-body">
                <p className="mb-0">
                  {orderDetails.shippingAddress}
                  <br />
                  {orderDetails.shippingCity}, {orderDetails.shippingState} {orderDetails.shippingZip}
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="d-flex justify-content-between">
            <button
              className="btn btn-primary"
              onClick={() => navigate('/wholesale/orders')}
            >
              <Download size={18} className="me-2" />
              View Order History
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;